import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  calculateSystemV2Price,
  calculateSubtypePrice,
} from '../../api';
import type { ProductGroup, ProductVariant, PricePoint } from '../../types';
import {
  resolveSelectorSchema,
  showWiredPowerChip,
  protocolsMatch,
  variantMatchesSelection,
  pickBestVariant,
  minVariantMatrixPrice,
  variantProfileColor,
  variantMotorColor,
  needsTierConfiguratorStep,
  implicitTierSlug,
  usesSplitColorSteps,
  effectiveBatterySelection,
  dualPowerChoice,
  wiredOnlyPower,
  isColorLikeControlTag,
  protocolGroupOptionSet,
  type VariantLike,
  type ProtocolGroup,
} from '../../lib/configuratorSchema';
import {
  autoControlTagsForProtocol,
  controlIncompatReason,
  type ControlSelection,
} from '../../lib/controlCompatibility';
import {
  analyzePriceMatrix,
  evaluateMatrixDimensions,
  matrixPointPrice,
  resolveMatrixDimLabels,
  roundUpOnGrid,
  secondaryValuesForPrimary,
  sizeSelectionComplete,
  type DimensionQuote,
  type MatrixDimKey,
  type MatrixLayout,
} from '../../lib/priceMatrixDimensions';

interface CfgTouched {
  tier: boolean;
  power: boolean;
  opening: boolean;
  iface: boolean;
  protocol: boolean;
  control: boolean;
  color: boolean;
  profile_color: boolean;
  motor_color: boolean;
}

interface UseConfiguratorResult {
  schemaConfig: ReturnType<typeof resolveSelectorSchema>;
  schemaSteps: string[] | null;
  showWiredPowerChip: boolean;
  dualPowerChoice: boolean;
  wiredOnlyPower: boolean;
  splitColorSteps: boolean;
  selectedTierSlug: string | null;
  selectedBattery: boolean | null;
  selectedIfaceType: string | null;
  selectedProtocol: string | null;
  selectedControlTags: string[];
  selectedColor: string | null;
  selectedProfileColor: string | null;
  selectedMotorColor: string | null;
  selectedOpening: string | null;
  selectedDim: number | null;
  selectedSecondDim: number | null;
  customDimInput: string;
  customSecondaryDimInput: string;
  dimensionQuote: DimensionQuote;
  clientHeightM: string;
  cfgTouched: CfgTouched;
  availableTiers: string[];
  showTierChoice: boolean;
  availableIfaceTypes: string[];
  availableProtocols: string[];
  availableControlTags: string[];
  availableColors: string[];
  availableProfileColors: string[];
  availableMotorColors: string[];
  protocolGroups: ProtocolGroup[];
  visibleProtocolGroups: ProtocolGroup[];
  activeVariant: ProductVariant | null;
  matchAmbiguous: boolean;
  matrix: PricePoint[];
  matrixLayout: MatrixLayout;
  dimKey: MatrixDimKey | null;
  secondDimKey: MatrixDimKey | null;
  uniqueDims: number[];
  uniqueSecondaryDims: number[];
  dimLabels: { primary: string; secondary: string };
  maxDim: number | null;
  exceedsCatalogRange: boolean;
  roundedSelectedDim: number | null;
  roundedSelectedSecondDim: number | null;
  dimensionValueForV2Api: number | null;
  secondDimensionValueForV2Api: number | null;
  showRoomHeightNote: boolean;
  selectedPrice: number | null;
  v2Price: number | null;
  v2PriceLoading: boolean;
  v2BasePrice: number | null;
  v2Surcharges: Array<{ code: string; label: string; amount: number }>;
  animatedPrice: number;
  animatedCatalogPrice: number;
  animatedCheckoutPrice: number;
  pricePulse: boolean;
  displayPrice: number;
  catalogRetailPrice: number;
  checkoutPrice: number;
  effectiveDiscountPct: number;
  hasPartnerDiscount: boolean;
  discountFactor: number;
  retailPrice: number;
  savingsPrice: number;
  cfgConfiguratorStarted: boolean;
  showCheckoutDock: boolean;
  cfgReveal: {
    showPower: boolean;
    showOpening: boolean;
    showControl: boolean;
    showColors: boolean;
    showSize: boolean;
  };
  variantSelection: Parameters<typeof variantMatchesSelection>[1];
  isChipDisabled: (kind: 'tier' | 'control' | 'protocol' | 'profile' | 'motor' | 'iface', value: string, groupId?: string) => boolean;
  controlDisableReason: (kind: 'protocol' | 'control', value: string, groupId?: string) => string | null;
  toggleControlTag: (tag: string) => void;
  selectProtocolInGroup: (proto: string, group: ProtocolGroup) => void;
  setSelectedTierSlug: (v: string | null) => void;
  setSelectedBattery: (v: boolean | null) => void;
  setSelectedIfaceType: (v: string | null) => void;
  setSelectedProtocol: (v: string | null) => void;
  setSelectedColor: (v: string | null) => void;
  setSelectedProfileColor: (v: string | null) => void;
  setSelectedMotorColor: (v: string | null) => void;
  setSelectedOpening: (v: string | null) => void;
  setSelectedDim: (v: number | null) => void;
  setSelectedSecondDim: (v: number | null) => void;
  setCustomDimInput: (v: string) => void;
  setCustomSecondaryDimInput: (v: string) => void;
  setClientHeightM: (v: string) => void;
  setCfgTouched: React.Dispatch<React.SetStateAction<CfgTouched>>;
}

function norm(s: string | null | undefined) {
  return String(s ?? '').trim().toLowerCase();
}

function asVariants(group: ProductGroup | null): VariantLike[] {
  return (group?.variants ?? []) as VariantLike[];
}

export function useConfigurator(
  group: ProductGroup | null,
  effectiveGroupId: string | null,
  effectiveCatalogSlug: string | null,
  discountPct: number | null | undefined,
  options?: {
    isSliding?: boolean;
    catalogTierSlug?: string | null;
    branchSlug?: string | null;
    subtypeSlug?: string | null;
  },
): UseConfiguratorResult {
  const isSliding = options?.isSliding ?? false;
  const catalogTierSlug = options?.catalogTierSlug ?? null;
  const [selectedTierSlug, setSelectedTierSlug] = useState<string | null>(null);
  const [selectedBattery, setSelectedBattery] = useState<boolean | null>(null);
  const [selectedIfaceType, setSelectedIfaceType] = useState<string | null>(null);
  const [selectedProtocol, setSelectedProtocol] = useState<string | null>(null);
  const [selectedControlTags, setSelectedControlTags] = useState<string[]>([]);
  const [selectedDim, setSelectedDim] = useState<number | null>(null);
  const [selectedSecondDim, setSelectedSecondDim] = useState<number | null>(null);
  const [customDimInput, setCustomDimInput] = useState<string>('');
  const [customSecondaryDimInput, setCustomSecondaryDimInput] = useState<string>('');
  const [clientHeightM, setClientHeightM] = useState<string>('');
  const [selectedOpening, setSelectedOpening] = useState<string | null>(null);
  const [selectedColor, setSelectedColor] = useState<string | null>(null);
  const [selectedProfileColor, setSelectedProfileColor] = useState<string | null>(null);
  const [selectedMotorColor, setSelectedMotorColor] = useState<string | null>(null);
  const [cfgTouched, setCfgTouched] = useState<CfgTouched>({
    tier: false, power: false, opening: false, iface: false, protocol: false,
    control: false, color: false, profile_color: false, motor_color: false,
  });
  const [v2Price, setV2Price] = useState<number | null>(null);
  const [v2RetailSubtotal, setV2RetailSubtotal] = useState<number | null>(null);
  const [v2PartnerFinal, setV2PartnerFinal] = useState<number | null>(null);
  const [v2PriceLoading, setV2PriceLoading] = useState(false);
  const [v2BasePrice, setV2BasePrice] = useState<number | null>(null);
  const [v2Surcharges, setV2Surcharges] = useState<Array<{ code: string; label: string; amount: number }>>([]);
  const [animatedCatalogPrice, setAnimatedCatalogPrice] = useState<number>(0);
  const [animatedCheckoutPrice, setAnimatedCheckoutPrice] = useState<number>(0);
  const [pricePulse, setPricePulse] = useState(false);
  const prevBatteryRef = useRef<boolean | null | undefined>(undefined);
  const prevOpeningRef = useRef<string | null | undefined>(undefined);
  const skipAutoProtocolOnceRef = useRef(false);

  const clearControlAndColors = useCallback(() => {
    setSelectedProtocol(null);
    setSelectedControlTags([]);
    setSelectedIfaceType(null);
    setSelectedColor(null);
    setSelectedProfileColor(null);
    setSelectedMotorColor(null);
    setSelectedDim(null);
    setSelectedSecondDim(null);
    setCustomDimInput('');
    setCfgTouched((p) => ({
      ...p,
      protocol: false,
      control: false,
      iface: false,
      color: false,
      profile_color: false,
      motor_color: false,
    }));
  }, []);

  const schemaConfig = useMemo(
    () =>
      resolveSelectorSchema(group?.selector_schema as Record<string, unknown> | undefined, {
        isSliding,
        branchSlug: options?.branchSlug ?? group?.branch_slug ?? null,
        subtypeSlug: options?.subtypeSlug ?? group?.subtype_slug ?? null,
        groupId: effectiveGroupId,
      }),
    [
      group?.selector_schema,
      group?.branch_slug,
      group?.subtype_slug,
      isSliding,
      effectiveGroupId,
      options?.branchSlug,
      options?.subtypeSlug,
    ],
  );

  const schemaSteps = useMemo(
    () => (schemaConfig.steps?.length ? (schemaConfig.steps as string[]) : null),
    [schemaConfig.steps],
  );

  const splitColorSteps = usesSplitColorSteps(schemaConfig);
  const dualPower = dualPowerChoice(group);
  const wiredOnly = wiredOnlyPower(group);

  const availableTiers = useMemo(() => {
    const fromApi = group?.available_tier_slugs;
    if (Array.isArray(fromApi) && fromApi.length > 0) {
      return fromApi.map((t) => String(t));
    }
    const slugs = new Set(asVariants(group).map((v) => v.tier_slug).filter(Boolean) as string[]);
    return [...slugs].sort();
  }, [group]);

  const showTierChoice = useMemo(() => needsTierConfiguratorStep(availableTiers), [availableTiers]);
  const implicitTier = useMemo(() => implicitTierSlug(availableTiers), [availableTiers]);

  const effectiveBattery = useMemo(
    () =>
      effectiveBatterySelection(
        group,
        selectedBattery,
        cfgTouched.power,
      ),
    [group, selectedBattery, cfgTouched.power],
  );

  const variantSelection = useMemo(
    () => ({
      tierSlug: showTierChoice ? selectedTierSlug : implicitTier,
      battery: effectiveBattery,
      openingType: cfgTouched.opening ? selectedOpening : null,
      ifaceType: cfgTouched.iface ? selectedIfaceType : null,
      protocol:
        cfgTouched.protocol || (cfgTouched.control && selectedProtocol)
          ? selectedProtocol
          : null,
      wiredOnly:
        Boolean(
          cfgTouched.control
          && !selectedProtocol
          && !cfgTouched.protocol,
        ),
      controlTags: cfgTouched.control ? selectedControlTags : [],
      profileColor: cfgTouched.profile_color ? selectedProfileColor : null,
      motorColor: cfgTouched.motor_color ? selectedMotorColor : null,
      legacyColor: splitColorSteps ? null : (cfgTouched.color ? selectedColor : null),
    }),
    [
      group, showTierChoice, implicitTier, splitColorSteps, effectiveBattery, selectedTierSlug, selectedOpening,
      selectedIfaceType, selectedProtocol, selectedControlTags, selectedProfileColor,
      selectedMotorColor, selectedColor, cfgTouched,
    ],
  );

  const baseFiltered = useMemo(() => {
    const all = asVariants(group);
    return all.filter((v) => variantMatchesSelection(v, variantSelection));
  }, [group, variantSelection]);

  const availableIfaceTypes = useMemo(() => {
    const pool = baseFiltered.length ? baseFiltered : asVariants(group);
    return [...new Set(pool.map((v) => v.interface_type).filter(Boolean) as string[])];
  }, [group, baseFiltered]);

  const availableProtocols = useMemo(() => {
    const controlOnly =
      (schemaConfig.protocol_groups?.length ?? 0) === 0
      && (schemaConfig.control_tags?.length ?? 0) > 0;
    if (controlOnly) return [];
    const pool = baseFiltered.length ? baseFiltered : asVariants(group);
    const fromVariants = [...new Set(pool.map((v) => v.protocol).filter(Boolean) as string[])];
    const list = schemaConfig.protocols?.length
      ? fromVariants.filter((p) => schemaConfig.protocols!.includes(p))
      : fromVariants;
    return list.filter((p) => p !== 'Wi-Fi (MiHome)');
  }, [group, baseFiltered, schemaConfig.protocols, schemaConfig.protocol_groups, schemaConfig.control_tags]);

  const availableControlTags = useMemo(() => {
    const fromSchema = schemaConfig.control_tags ?? [];
    const fromVariants = new Set<string>();
    for (const v of asVariants(group)) {
      for (const t of v.control_types ?? []) {
        if (t) fromVariants.add(String(t).trim());
      }
    }
    const pgOpts = protocolGroupOptionSet(schemaConfig.protocol_groups ?? []);
    const union = [...new Set([...fromSchema, ...fromVariants])].filter((tag) => {
      if (isColorLikeControlTag(tag)) return false;
      if (pgOpts.size > 0 && pgOpts.has(norm(tag))) return false;
      return true;
    });
    return union.sort();
  }, [group, schemaConfig.control_tags, schemaConfig.protocol_groups]);

  const availableColors = useMemo(() => {
    const pool = baseFiltered.length ? baseFiltered : asVariants(group);
    const fromVariants = [...new Set(pool.map((v) => v.color).filter(Boolean) as string[])];
    if (schemaConfig.colors?.length) {
      return schemaConfig.colors.filter((c) => fromVariants.includes(c) || fromVariants.length === 0);
    }
    return fromVariants;
  }, [group, baseFiltered, schemaConfig.colors]);

  const availableProfileColors = useMemo(() => {
    const pool = baseFiltered.length ? baseFiltered : asVariants(group);
    const fromVariants = [...new Set(pool.map((v) => variantProfileColor(v)).filter(Boolean) as string[])];
    if (schemaConfig.profile_colors?.length) {
      return schemaConfig.profile_colors.filter((c) => fromVariants.includes(c) || !fromVariants.length);
    }
    return fromVariants;
  }, [group, baseFiltered, schemaConfig.profile_colors]);

  const availableMotorColors = useMemo(() => {
    const pool = baseFiltered.length ? baseFiltered : asVariants(group);
    const fromVariants = [...new Set(pool.map((v) => variantMotorColor(v)).filter(Boolean) as string[])];
    if (schemaConfig.motor_colors?.length) {
      return schemaConfig.motor_colors.filter((c) => fromVariants.includes(c) || !fromVariants.length);
    }
    return fromVariants;
  }, [group, baseFiltered, schemaConfig.motor_colors]);

  const protocolGroups = useMemo(() => {
    const deprecated = new Set(['Wi-Fi (MiHome)']);
    return (schemaConfig.protocol_groups ?? [])
      .map((pg) => ({ ...pg, options: pg.options.filter((o) => !deprecated.has(o)) }))
      .filter((pg) => pg.options.length > 0);
  }, [schemaConfig.protocol_groups]);

  const cfgConfiguratorStarted =
    cfgTouched.tier || cfgTouched.power || cfgTouched.opening || cfgTouched.iface
    || cfgTouched.protocol || cfgTouched.control || cfgTouched.color
    || cfgTouched.profile_color || cfgTouched.motor_color;

  /** Пилюля цены — только после выбора в блоке «Конфигурация» (220В, ZigBee…), не от навигатора типа. */
  const showCheckoutDock = useMemo(() => {
    if (!group?.variants?.length) return false;
    if (showTierChoice && cfgTouched.tier && selectedTierSlug) return true;
    if (cfgTouched.power && selectedBattery !== null) return true;
    if (cfgTouched.opening && selectedOpening) return true;
    if (cfgTouched.protocol && selectedProtocol) return true;
    if (cfgTouched.control && (selectedControlTags.length > 0 || selectedProtocol)) return true;
    if (cfgTouched.profile_color && selectedProfileColor) return true;
    if (cfgTouched.motor_color && selectedMotorColor) return true;
    if (cfgTouched.color && selectedColor) return true;
    if (selectedDim !== null) return true;
    return false;
  }, [
    group?.variants?.length,
    showTierChoice,
    cfgTouched,
    selectedTierSlug,
    selectedBattery,
    selectedOpening,
    selectedProtocol,
    selectedControlTags,
    selectedProfileColor,
    selectedMotorColor,
    selectedColor,
    selectedDim,
  ]);

  const tierGateDone =
    !showTierChoice || availableTiers.length === 0 || (cfgTouched.tier && selectedTierSlug !== null);
  const powerGateDone = useMemo(() => {
    if (!group?.has_wired) return true;
    if (wiredOnly) return cfgTouched.power && selectedBattery === false;
    if (dualPower) return cfgTouched.power && selectedBattery !== null;
    return true;
  }, [group?.has_wired, wiredOnly, dualPower, cfgTouched.power, selectedBattery]);
  const openingGateDone = !isSliding || (cfgTouched.opening && selectedOpening !== null);
  const controlGateDone =
    cfgTouched.control
    && (selectedProtocol !== null || selectedControlTags.length > 0);
  const colorsGateDone =
    (availableProfileColors.length === 0 || (cfgTouched.profile_color && selectedProfileColor !== null))
    && (availableMotorColors.length === 0 || (cfgTouched.motor_color && selectedMotorColor !== null))
    && (
      splitColorSteps
      || availableColors.length === 0
      || (cfgTouched.color && selectedColor !== null)
    );

  const cfgReveal = {
    showPower: tierGateDone && (dualPower || wiredOnly),
    showOpening: tierGateDone && powerGateDone && isSliding,
    showControl: tierGateDone && powerGateDone && openingGateDone,
    showColors: tierGateDone && powerGateDone && openingGateDone && controlGateDone,
    showSize: tierGateDone && powerGateDone && openingGateDone && controlGateDone && colorsGateDone,
  };

  const { activeVariant, matchAmbiguous } = useMemo(() => {
    if (!group || !group.variants.length || !cfgConfiguratorStarted) {
      return { activeVariant: null as ProductVariant | null, matchAmbiguous: false };
    }
    const matched = baseFiltered;
    if (matched.length === 0) return { activeVariant: null, matchAmbiguous: false };
    if (matched.length === 1) return { activeVariant: matched[0] as ProductVariant, matchAmbiguous: false };
    const best = pickBestVariant(matched, variantSelection);
    return { activeVariant: (best ?? matched[0]) as ProductVariant, matchAmbiguous: true };
  }, [group, cfgConfiguratorStarted, baseFiltered, variantSelection]);

  /** Смена 220В ↔ АКБ: сброс управления/цветов/размера; направление открывания не трогаем. */
  useEffect(() => {
    if (!cfgTouched.power || selectedBattery === null) {
      if (!cfgTouched.power) prevBatteryRef.current = undefined;
      return;
    }
    const prev = prevBatteryRef.current;
    if (prev !== undefined && prev !== selectedBattery) {
      skipAutoProtocolOnceRef.current = true;
      clearControlAndColors();
    }
    prevBatteryRef.current = selectedBattery;
  }, [selectedBattery, cfgTouched.power, clearControlAndColors]);

  /** Смена направления: сброс управления (питание сохраняем). */
  useEffect(() => {
    if (!cfgTouched.opening || !selectedOpening) {
      if (!cfgTouched.opening) prevOpeningRef.current = undefined;
      return;
    }
    const prev = prevOpeningRef.current;
    if (prev !== undefined && prev !== selectedOpening) {
      skipAutoProtocolOnceRef.current = true;
      clearControlAndColors();
    }
    prevOpeningRef.current = selectedOpening;
  }, [selectedOpening, cfgTouched.opening, clearControlAndColors]);

  useEffect(() => {
    if (!group || !cfgTouched.power || selectedBattery === null) return;
    if (skipAutoProtocolOnceRef.current) {
      skipAutoProtocolOnceRef.current = false;
      return;
    }
    const pool = asVariants(group).filter((v) =>
      variantMatchesSelection(v, {
        tierSlug: showTierChoice ? selectedTierSlug : implicitTier,
        battery: selectedBattery,
        openingType: cfgTouched.opening ? selectedOpening : null,
      }),
    );
    const protos = [...new Set(pool.map((v) => v.protocol).filter(Boolean) as string[])];
    if (selectedProtocol && !protos.some((p) => protocolsMatch(p, selectedProtocol))) {
      setSelectedProtocol(null);
      setSelectedControlTags([]);
      setCfgTouched((p) => ({ ...p, protocol: false, control: false }));
      return;
    }
    if (protos.length === 1 && !selectedProtocol && protos[0]) {
      const only = protos[0]!;
      setSelectedProtocol(only);
      setSelectedControlTags((prev) => [...new Set([...prev, ...autoControlTagsForProtocol(only)])]);
      setCfgTouched((p) => ({ ...p, protocol: true, control: true }));
    }
  }, [
    group,
    showTierChoice,
    implicitTier,
    selectedTierSlug,
    cfgTouched.power,
    cfgTouched.opening,
    selectedBattery,
    selectedOpening,
    selectedProtocol,
  ]);

  useEffect(() => {
    prevBatteryRef.current = undefined;
    prevOpeningRef.current = undefined;
    skipAutoProtocolOnceRef.current = false;
    setSelectedTierSlug(null);
    setSelectedBattery(null);
    setSelectedIfaceType(null);
    setSelectedProtocol(null);
    setSelectedControlTags([]);
    setSelectedDim(null);
    setSelectedSecondDim(null);
    setCustomDimInput('');
    setClientHeightM('');
    setSelectedOpening(null);
    setSelectedColor(null);
    setSelectedProfileColor(null);
    setSelectedMotorColor(null);
    setCfgTouched({
      tier: false, power: false, opening: false, iface: false, protocol: false,
      control: false, color: false, profile_color: false, motor_color: false,
    });
    if (group) {
      const tierSlugs = [
        ...new Set(asVariants(group).map((v) => v.tier_slug).filter(Boolean) as string[]),
      ];
      const urlTier = catalogTierSlug?.trim() || null;
      if (urlTier && needsTierConfiguratorStep(tierSlugs)) {
        setSelectedTierSlug(urlTier);
        setCfgTouched((p) => ({ ...p, tier: true }));
      } else if (!needsTierConfiguratorStep(tierSlugs)) {
        const implicit = implicitTierSlug(tierSlugs);
        if (implicit) setSelectedTierSlug(implicit);
      }
    }
  }, [effectiveGroupId, group, catalogTierSlug]);

  const currentControlSel = useMemo<ControlSelection>(
    () => ({ protocol: selectedProtocol, controlTags: selectedControlTags }),
    [selectedProtocol, selectedControlTags],
  );

  const isChipDisabled = useCallback(
    (kind: 'tier' | 'control' | 'protocol' | 'profile' | 'motor' | 'iface', value: string, _groupId?: string) => {
      if (!group) return true;
      if (kind === 'protocol') {
        const reason = controlIncompatReason(currentControlSel, { kind: 'protocol', value });
        if (reason) return true;
      }
      const probe = (v: VariantLike) => {
        const sel = {
          ...variantSelection,
          tierSlug: kind === 'tier' ? value : selectedTierSlug,
          ifaceType: kind === 'iface' ? value : (cfgTouched.iface ? selectedIfaceType : null),
          protocol: kind === 'protocol' ? value : selectedProtocol,
          wiredOnly: kind === 'protocol' ? false : variantSelection.wiredOnly,
          controlTags:
            kind === 'control'
              ? selectedControlTags.includes(value)
                ? selectedControlTags.filter((t) => t !== value)
                : [...selectedControlTags, value]
              : selectedControlTags,
          profileColor: kind === 'profile' ? value : selectedProfileColor,
          motorColor: kind === 'motor' ? value : selectedMotorColor,
          legacyColor: selectedColor,
        };
        return variantMatchesSelection(v, sel);
      };
      return !asVariants(group).some(probe);
    },
    [
      group, variantSelection, selectedTierSlug, selectedBattery, selectedOpening, selectedIfaceType,
      selectedProtocol, selectedControlTags, selectedProfileColor, selectedMotorColor, selectedColor,
      cfgTouched, currentControlSel,
    ],
  );

  const controlDisableReason = useCallback(
    (kind: 'protocol' | 'control', value: string, _groupId?: string): string | null => {
      const logical = controlIncompatReason(currentControlSel, { kind, value });
      if (logical) return logical;
      if (isChipDisabled(kind === 'protocol' ? 'protocol' : 'control', value)) {
        return 'Нет в прайсе для этой комбинации';
      }
      return null;
    },
    [currentControlSel, isChipDisabled],
  );

  /** Только плашки, для которых есть строки в прайсе — без серых «мертвых» чипов. */
  const visibleProtocolGroups = useMemo(() => {
    return protocolGroups
      .map((pg) => {
        const isWireless = pg.exclusive;
        const options = pg.options.filter((proto) => {
          const kind = isWireless ? ('protocol' as const) : ('control' as const);
          return !isChipDisabled(kind, proto, pg.id);
        });
        return { ...pg, options };
      })
      .filter((pg) => pg.options.length > 0);
  }, [protocolGroups, isChipDisabled]);

  const toggleControlTag = useCallback((tag: string) => {
    setCfgTouched((p) => ({ ...p, control: true }));
    setSelectedControlTags((prev) => {
      const next = prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag];
      return next;
    });
  }, []);

  const selectProtocolInGroup = useCallback((proto: string, groupDef: ProtocolGroup) => {
    setCfgTouched((p) => ({ ...p, control: true, protocol: true }));
    const turningOff = norm(selectedProtocol) === norm(proto);
    if (groupDef.exclusive) {
      setSelectedProtocol(turningOff ? null : proto);
      if (!turningOff) {
        const bundled = autoControlTagsForProtocol(proto);
        setSelectedControlTags((prev) => [...new Set([...prev, ...bundled])]);
      }
    } else {
      toggleControlTag(proto);
      return;
    }
    setSelectedColor(null);
    setSelectedProfileColor(null);
    setSelectedMotorColor(null);
  }, [selectedProtocol, toggleControlTag]);

  const matrix: PricePoint[] = useMemo(() => {
    if (activeVariant?.price_matrix && activeVariant.price_matrix.length > 0) {
      return activeVariant.price_matrix;
    }
    const gm = (group as unknown as { price_matrix?: PricePoint[] })?.price_matrix;
    return Array.isArray(gm) && gm.length > 0 ? gm : [];
  }, [activeVariant, group]);

  const matrixLayout = useMemo(() => analyzePriceMatrix(matrix), [matrix]);

  const dimKey: MatrixDimKey | null =
    matrixLayout.mode === '1d'
      ? matrixLayout.key
      : matrixLayout.mode === '2d'
        ? matrixLayout.primary
        : null;
  const secondDimKey: MatrixDimKey | null =
    matrixLayout.mode === '2d' ? matrixLayout.secondary : null;

  const uniqueDims = useMemo(() => {
    if (matrixLayout.mode === '1d') return matrixLayout.values;
    if (matrixLayout.mode === '2d') return matrixLayout.primaryValues;
    return [];
  }, [matrixLayout]);

  const uniqueSecondaryDims = useMemo(() => {
    if (matrixLayout.mode !== '2d' || selectedDim == null) return [];
    const gridW = roundUpOnGrid(selectedDim, matrixLayout.primaryValues);
    if (gridW == null) return [];
    return secondaryValuesForPrimary(matrix, matrixLayout.primary, matrixLayout.secondary, gridW);
  }, [matrix, matrixLayout, selectedDim]);

  const dimLabels = useMemo(
    () =>
      resolveMatrixDimLabels({
        layout: matrixLayout,
        schema: group?.selector_schema as Record<string, unknown> | undefined,
        category: group?.category,
        branchSlug: group?.branch_slug ?? (effectiveGroupId?.includes('::') ? effectiveGroupId.split('::')[0] : null),
      }),
    [matrixLayout, group?.selector_schema, group?.category, group?.branch_slug, effectiveGroupId],
  );

  const showRoomHeightNote =
    matrixLayout.mode === '1d' &&
    matrixLayout.key === 'length_m' &&
    !`${group?.category ?? ''}`.toLowerCase().includes('zip');

  const maxDim = uniqueDims.length > 0 ? uniqueDims[uniqueDims.length - 1] : null;
  const dimensionQuote = useMemo(
    () =>
      evaluateMatrixDimensions(matrixLayout, matrix, selectedDim, selectedSecondDim, dimLabels),
    [matrixLayout, matrix, selectedDim, selectedSecondDim, dimLabels],
  );

  const exceedsCatalogRange = dimensionQuote.priceOnRequest;

  const roundedSelectedDim =
    selectedDim !== null && uniqueDims.length > 0
      ? (uniqueDims.find((d) => d >= selectedDim) ?? uniqueDims[uniqueDims.length - 1] ?? null)
      : null;

  const roundedSelectedSecondDim =
    matrixLayout.mode === '2d' &&
    selectedSecondDim !== null &&
    uniqueSecondaryDims.length > 0
      ? (uniqueSecondaryDims.find((d) => d >= selectedSecondDim) ??
          uniqueSecondaryDims[uniqueSecondaryDims.length - 1] ??
          null)
      : null;

  const dimensionValueForV2Api = selectedDim;
  const secondDimensionValueForV2Api =
    matrixLayout.mode === '2d' ? selectedSecondDim : null;

  const selectedPrice = useMemo(() => {
    if (dimensionQuote.price != null) return dimensionQuote.price;
    if (matrixLayout.mode === '2d' && roundedSelectedDim != null && roundedSelectedSecondDim != null) {
      return matrixPointPrice(
        matrix,
        matrixLayout.primary,
        matrixLayout.secondary,
        roundedSelectedDim,
        roundedSelectedSecondDim,
      );
    }
    if (roundedSelectedDim != null && dimKey) {
      return matrixPointPrice(matrix, dimKey, null, roundedSelectedDim, null);
    }
    return null;
  }, [matrix, matrixLayout, dimKey, roundedSelectedDim, roundedSelectedSecondDim, dimensionQuote.price]);
  const activeVariantMinPrice = activeVariant ? minVariantMatrixPrice(activeVariant) : Number.POSITIVE_INFINITY;
  const matrixMinPrice = matrix.length ? Math.min(...matrix.map((p) => p.price)) : 0;
  const minPrice =
    Number.isFinite(activeVariantMinPrice) && activeVariantMinPrice < Number.POSITIVE_INFINITY
      ? activeVariantMinPrice
      : (group?.min_price ?? matrixMinPrice);
  const effectiveDiscountPct = Math.max(0, Number(discountPct || 0));
  const hasPartnerDiscount = effectiveDiscountPct > 0;
  const discountFactor = Math.max(0.01, 1 - effectiveDiscountPct / 100);

  /** Розница (РРЦ): матрица и калькулятор — всегда без партнёрской скидки. */
  const catalogRetailPrice = v2RetailSubtotal ?? selectedPrice ?? minPrice;
  /** Пилюля / корзина: партнёрская финальная, иначе розница. */
  const checkoutPrice = hasPartnerDiscount
    ? (v2PartnerFinal ?? Math.round(catalogRetailPrice * discountFactor))
    : catalogRetailPrice;
  const displayPrice = catalogRetailPrice;
  const retailPrice = catalogRetailPrice;
  const savingsPrice = Math.max(0, retailPrice - checkoutPrice);

  useEffect(() => {
    setSelectedDim(null);
    setSelectedSecondDim(null);
  }, [activeVariant?.id]);
  useEffect(() => {
    setCustomDimInput('');
    setCustomSecondaryDimInput('');
    setClientHeightM('');
  }, [activeVariant?.id, dimKey, secondDimKey]);

  useEffect(() => {
    setAnimatedCatalogPrice(catalogRetailPrice);
    setAnimatedCheckoutPrice(checkoutPrice);
  }, [effectiveGroupId]);

  const animateAmount = useCallback((from: number, to: number, setter: (v: number) => void) => {
    if (!Number.isFinite(from) || !Number.isFinite(to)) return;
    if (Math.abs(to - from) < 1) {
      setter(to);
      return;
    }
    setPricePulse(true);
    const durationMs = 520;
    const start = performance.now();
    let rafId = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs);
      const eased = 1 - Math.pow(1 - t, 4);
      setter(from + (to - from) * eased);
      if (t < 1) rafId = requestAnimationFrame(tick);
      else {
        setter(to);
        window.setTimeout(() => setPricePulse(false), 260);
      }
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, []);

  useEffect(() => {
    return animateAmount(animatedCatalogPrice, catalogRetailPrice, setAnimatedCatalogPrice);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogRetailPrice]);

  useEffect(() => {
    return animateAmount(animatedCheckoutPrice, checkoutPrice, setAnimatedCheckoutPrice);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutPrice]);

  useEffect(() => {
    const isV3BranchGroup =
      !!effectiveGroupId?.includes('::') && !effectiveGroupId.startsWith('v2::');
    const isSubtypeDirect = !!(group?.subtype_id && (effectiveGroupId?.startsWith('st::') || isV3BranchGroup));
    if (!isSubtypeDirect && !effectiveCatalogSlug) return;
    if (!group) return;
    const hasLegacyVariants = Array.isArray(group.variants) && group.variants.length > 0;
    const hasPriceMatrix = matrix.length > 0;
    if (!hasLegacyVariants && !hasPriceMatrix && !isSubtypeDirect && !cfgConfiguratorStarted) {
      setV2Price(null); setV2RetailSubtotal(null); setV2PartnerFinal(null);
      setV2BasePrice(null); setV2Surcharges([]); setV2PriceLoading(false);
      return;
    }
    const canPriceBySize = sizeSelectionComplete(matrixLayout, selectedDim, selectedSecondDim);
    if (!canPriceBySize || !dimensionQuote.canPrice) {
      setV2Price(null); setV2RetailSubtotal(null); setV2PartnerFinal(null);
      setV2BasePrice(null); setV2Surcharges([]); return;
    }
    const clientH = clientHeightM ? parseFloat(clientHeightM) : null;
    const billPrimary = dimensionQuote.billedPrimary ?? dimensionValueForV2Api;
    const billSecondary = dimensionQuote.billedSecondary ?? secondDimensionValueForV2Api;
    const pricingBody: Record<string, unknown> = {
      interface_type: selectedIfaceType ?? undefined,
      protocol: selectedProtocol ?? undefined,
      color: selectedColor ?? selectedProfileColor ?? undefined,
      profile_color: selectedProfileColor ?? undefined,
      motor_color: selectedMotorColor ?? undefined,
      tier_slug: selectedTierSlug ?? undefined,
      control_types: selectedControlTags.length > 0 ? selectedControlTags : undefined,
      opening_type: selectedOpening ?? undefined,
      ...(dimKey && billPrimary !== null ? { [dimKey]: billPrimary } : {}),
      ...(secondDimKey && billSecondary != null ? { [secondDimKey]: billSecondary } : {}),
      ...(clientH && Number.isFinite(clientH) && clientH > 0 && showRoomHeightNote
        ? { height_m: clientH }
        : {}),
    };
    let cancelled = false;
    setV2PriceLoading(true);
    const priceCall =
      group?.subtype_id && (effectiveGroupId?.startsWith('st::') || isV3BranchGroup)
        ? calculateSubtypePrice(group.subtype_id, pricingBody)
        : calculateSystemV2Price(
            effectiveCatalogSlug ?? '',
            pricingBody as Parameters<typeof calculateSystemV2Price>[1],
          );
    priceCall
      .then((res) => {
        if (cancelled) return;
        const finalPrice = Number(res?.pricing?.final_price);
        const subtotal = Number(res?.pricing?.subtotal);
        const basePrice = Number(res?.pricing?.base_price);
        const surcharges = Array.isArray(res?.surcharges)
          ? res.surcharges
              .map((s: { code?: string; label?: string; amount?: number }) => ({
                code: String(s?.code ?? ''),
                label: String(s?.label ?? ''),
                amount: Number(s?.amount ?? 0),
              }))
              .filter((s: { amount: number }) => Number.isFinite(s.amount) && s.amount > 0)
          : [];
        setV2RetailSubtotal(Number.isFinite(subtotal) ? subtotal : null);
        setV2PartnerFinal(Number.isFinite(finalPrice) ? finalPrice : null);
        setV2Price(Number.isFinite(finalPrice) ? finalPrice : null);
        setV2BasePrice(Number.isFinite(basePrice) ? basePrice : null);
        setV2Surcharges(surcharges);
      })
      .catch(() => {
        if (!cancelled) {
          setV2Price(null); setV2RetailSubtotal(null); setV2PartnerFinal(null);
          setV2BasePrice(null); setV2Surcharges([]);
        }
      })
      .finally(() => { if (!cancelled) setV2PriceLoading(false); });
    return () => { cancelled = true; };
  }, [
    effectiveCatalogSlug, group, cfgConfiguratorStarted, selectedIfaceType, selectedProtocol,
    selectedColor, selectedProfileColor, selectedMotorColor, selectedTierSlug, selectedControlTags,
    selectedOpening, selectedDim, selectedSecondDim, roundedSelectedDim, roundedSelectedSecondDim,
    dimensionValueForV2Api, secondDimensionValueForV2Api, dimKey, secondDimKey,
    matrixLayout, dimensionQuote.canPrice, clientHeightM, showRoomHeightNote, matrix.length, effectiveGroupId,
    dimensionQuote.billedPrimary, dimensionQuote.billedSecondary,
  ]);

  return {
    schemaConfig,
    schemaSteps,
    showWiredPowerChip: showWiredPowerChip(group, selectedBattery, cfgTouched.power),
    dualPowerChoice: dualPower,
    wiredOnlyPower: wiredOnly,
    splitColorSteps,
    selectedTierSlug, selectedBattery, selectedIfaceType, selectedProtocol, selectedControlTags,
    selectedColor, selectedProfileColor, selectedMotorColor, selectedOpening,
    selectedDim, customDimInput, customSecondaryDimInput, dimensionQuote, clientHeightM, cfgTouched,
    availableTiers, showTierChoice, availableIfaceTypes, availableProtocols, availableControlTags,
    availableColors, availableProfileColors, availableMotorColors,
    protocolGroups, visibleProtocolGroups,
    activeVariant, matchAmbiguous, matrix, matrixLayout, dimKey, secondDimKey, uniqueDims,
    uniqueSecondaryDims, dimLabels, maxDim, exceedsCatalogRange, roundedSelectedDim,
    roundedSelectedSecondDim, dimensionValueForV2Api, secondDimensionValueForV2Api, selectedPrice,
    showRoomHeightNote, selectedSecondDim, setSelectedSecondDim,
    v2Price, v2PriceLoading, v2BasePrice, v2Surcharges,
    animatedPrice: animatedCheckoutPrice,
    animatedCatalogPrice,
    animatedCheckoutPrice,
    pricePulse,
    displayPrice,
    catalogRetailPrice,
    checkoutPrice,
    effectiveDiscountPct,
    hasPartnerDiscount,
    discountFactor,
    retailPrice,
    savingsPrice,
    cfgConfiguratorStarted,
    showCheckoutDock,
    cfgReveal,
    variantSelection,
    isChipDisabled, controlDisableReason, toggleControlTag, selectProtocolInGroup,
    setSelectedTierSlug, setSelectedBattery, setSelectedIfaceType, setSelectedProtocol,
    setSelectedColor, setSelectedProfileColor, setSelectedMotorColor,
    setSelectedOpening, setSelectedDim, setCustomDimInput, setCustomSecondaryDimInput, setClientHeightM, setCfgTouched,
  };
}
